import { useEffect, useRef, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, onSnapshot, query, setDoc, updateDoc, where, writeBatch } from '../fs';
import { orgActual } from '../org';
import { db } from '../firebase';
import { usePanel } from '../App.jsx';
import { CLINICAS, nombreClinica } from '../clinicas';
import { hoyISO } from '../dia/comun';
import { festivo } from '../festivos';
import { validarTexto, validarFecha, validarCantidad, primerError } from '../validar.js';

// Vacaciones y horario: quién trabaja cada día en cada clínica, solicitudes de vacaciones y días que le quedan a cada uno.
// personal/{id}: nombre, puesto, clinicas[], uid (para pedir sus propias vacaciones), computo (convenio|manual), diasAnuales, fechaAlta, fechaBaja, horario {1..6,0: [{clinica, de, a, semanas}]}
// ausencias/{id}: personaId, persona, tipo, desde, hasta, estado (pendiente|aprobada|rechazada), nota

export const TIPOS = {
  vacaciones: ['🏖 Vacaciones', '#16a34a'], asuntos: ['📌 Asuntos propios', '#2563eb'], baja: ['🤒 Baja', '#dc2626'],
  formacion: ['🎓 Formación', '#7c3aed'], otro: ['• Otro', '#64748b'],
};
export const PUESTOS = { socio: 'Socio/a', vet: 'Veterinario/a', atv: 'ATV', peluqueria: 'Peluquería', recepcion: 'Recepción' };
const DIAS = [[1, 'Lunes'], [2, 'Martes'], [3, 'Miércoles'], [4, 'Jueves'], [5, 'Viernes'], [6, 'Sábado'], [0, 'Domingo']];
// Datos de cada clínica sacados de su ficha (se cargan al entrar; por eso se leen al usarlos, no al cargar el archivo)
const ficha = (id) => CLINICAS.find((c) => c.id === id);
const COLOR_CLIN = new Proxy({}, { get: (_, id) => ficha(id)?.color });
const INI_CLIN = new Proxy({}, { get: (_, id) => { const c = ficha(id); return c?.iniciales || String(c?.nombre || id).replace(/[^A-Za-zÁÉÍÓÚÑáéíóúñ ]/g, '').split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '·'; } });

// ---------- fechas ----------
const d12 = (s) => new Date(`${s}T12:00:00Z`);
const iso = (d) => d.toISOString().slice(0, 10);
const masDias = (s, n) => iso(new Date(d12(s).getTime() + n * 864e5));
const diaSem = (s) => d12(s).getUTCDay();
const semanaISO = (s) => { const d = d12(s); const j = (d.getUTCDay() + 6) % 7; d.setUTCDate(d.getUTCDate() - j + 3); const p = new Date(Date.UTC(d.getUTCFullYear(), 0, 4)); return 1 + Math.round(((d - p) / 864e5 - 3 + ((p.getUTCDay() + 6) % 7)) / 7); };
const rango = (a, b) => { const out = []; for (let s = a; s <= b && out.length < 400; s = masDias(s, 1)) out.push(s); return out; };
const fCorta = (s) => d12(s).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const hNum = (h) => { const [a, b] = String(h || '0:0').split(':'); return +a + (+b || 0) / 60; };
const horas = (t) => Math.max(0, hNum(t.a) - hNum(t.de));
const fmtH = (n) => `${Math.round(n * 10) / 10}`.replace('.', ',');

// ---------- lógica ----------
// Sábados: 'todas', 'A'/'B' (semanas alternas) o '13'/'24' (1º y 3º del mes / 2º y 4º; el 5º va con 1º y 3º)
const nSabado = (dia) => Math.ceil(+dia.slice(8) / 7);
export function tocaSemana(sem, dia) {
  if (!sem || sem === 'todas') return true;
  if (sem === '13') return nSabado(dia) % 2 === 1;
  if (sem === '24') return nSabado(dia) % 2 === 0;
  return (sem === 'A') === (semanaISO(dia) % 2 === 0);
}
// Sábados que abre cada clínica (ficha → bot.sabados: '1y3' = solo 1.º y 3.º del mes, '2y4' = 2.º y 4.º)
const SABADOS_CLINICA = new Proxy({}, { get: (_, id) => ({ '1y3': '13', '2y4': '24' })[ficha(id)?.bot?.sabados] });
// Tramos del horario base para un día (sin contar ausencias). Sábados alternos: semanas 'A' (pares) o 'B' (impares)
export function tramosDe(p, dia) {
  const ts = p.horario?.[diaSem(dia)] || [];
  return ts.filter((t) => tocaSemana(t.semanas, dia)).filter((t) => !festivo(t.clinica, dia));
}
export const ausenciaDe = (aus, pid, dia, soloAprobadas = true) => aus.find((a) => a.personaId === pid && a.desde <= dia && a.hasta >= dia && (soloAprobadas ? a.estado === 'aprobada' : a.estado !== 'rechazada'));
// Días que cuenta una ausencia: los que esa persona trabajaría (si no tiene horario, de lunes a viernes no festivos)
export function diasQueCuenta(p, desde, hasta) {
  const conHorario = Object.values(p.horario || {}).some((l) => l?.length);
  return rango(desde, hasta).filter((d) => (conHorario ? tramosDe(p, d).length > 0 : diaSem(d) >= 1 && diaSem(d) <= 5 && !festivo(p.clinicas?.[0], d))).length;
}
// Convenio colectivo estatal de centros y servicios veterinarios 2026-2028 (BOE 21/09/2026):
// 30 días naturales de vacaciones al año, 1 día de asuntos propios, jornada máxima anual 1.780 h (2026), 1.770 (2027), 1.760 (2028).
// Si entra o sale a mitad de año, las vacaciones se prorratean por los días en plantilla (redondeo al alza).
export const CONVENIO = { vacaciones: 30, asuntos: 1, horas: { 2026: 1780, 2027: 1770, 2028: 1760 } };
const diasEntre = (a, b) => Math.round((d12(b) - d12(a)) / 864e5) + 1;
export const porConvenio = (p) => (p.computo ? p.computo === 'convenio' : p.puesto !== 'socio');
export function saldo(p, aus, anio = hoyISO().slice(0, 4)) {
  const ini = `${anio}-01-01`, fin = `${anio}-12-31`, hoy = hoyISO();
  const desde = p.fechaAlta && p.fechaAlta > ini ? p.fechaAlta : ini;
  const hasta = p.fechaBaja && p.fechaBaja < fin ? p.fechaBaja : fin;
  const frac = hasta < desde ? 0 : diasEntre(desde, hasta) / diasEntre(ini, fin);
  const conv = porConvenio(p);
  const total = conv ? Math.ceil(CONVENIO.vacaciones * frac) : Number(p.computo === 'manual' ? p.diasAnuales ?? CONVENIO.vacaciones : CONVENIO.vacaciones);
  const clip = (a) => [a.desde < ini ? ini : a.desde, a.hasta > fin ? fin : a.hasta];
  const nat = (de, ha) => (ha < de ? 0 : diasEntre(de, ha));
  const suyas = aus.filter((a) => a.personaId === p.id && a.hasta >= ini && a.desde <= fin && a.estado !== 'rechazada');
  const vac = suyas.filter((a) => a.tipo === 'vacaciones');
  const aprob = vac.filter((a) => a.estado === 'aprobada');
  const disfrutados = aprob.reduce((n, a) => { const [de, ha] = clip(a); return n + nat(de, ha < hoy ? ha : masDias(hoy, -1)); }, 0);
  const aprobados = aprob.reduce((n, a) => n + nat(...clip(a)), 0);
  const pedidos = vac.filter((a) => a.estado === 'pendiente').reduce((n, a) => n + nat(...clip(a)), 0);
  const asuntosUsados = suyas.filter((a) => a.tipo === 'asuntos').reduce((n, a) => n + diasQueCuenta(p, ...clip(a)), 0);
  const asuntosTotal = conv ? CONVENIO.asuntos : null;
  return { total, disfrutados, aprobados, pedidos, quedan: total - aprobados, conv, frac, asuntosUsados, asuntosTotal };
}
// Horas que hará en el año según su horario base (sin festivos ni ausencias aprobadas) frente al máximo del convenio
export function horasAnio(p, aus, anio = hoyISO().slice(0, 4)) {
  const ini = `${anio}-01-01`, fin = `${anio}-12-31`;
  const desde = p.fechaAlta && p.fechaAlta > ini ? p.fechaAlta : ini;
  const hasta = p.fechaBaja && p.fechaBaja < fin ? p.fechaBaja : fin;
  let h = 0;
  for (const d of rango(desde, hasta)) if (!ausenciaDe(aus, p.id, d)) h += tramosDe(p, d).reduce((m, t) => m + horas(t), 0);
  const max = CONVENIO.horas[anio] ? Math.round(CONVENIO.horas[anio] * (hasta < desde ? 0 : diasEntre(desde, hasta) / diasEntre(ini, fin))) : null;
  return { h: Math.round(h), max };
}
// Días que gasta una ausencia: vacaciones en días naturales (convenio); el resto, días de trabajo
export const diasGasta = (p, a) => (a.tipo === 'vacaciones' ? diasEntre(a.desde, a.hasta) : diasQueCuenta(p, a.desde, a.hasta));
// ¿Abre la clínica ese día? (domingos, festivos, cierres y sábados alternos del bot)
function abre(clinica, dia, bot = {}) {
  if (diaSem(dia) === 0 || festivo(clinica, dia) || bot.cierres?.[dia]) return false;
  if (diaSem(dia) === 6 && SABADOS_CLINICA[clinica]) return tocaSemana(SABADOS_CLINICA[clinica], dia);
  if (diaSem(dia) === 6 && bot.sabadoRef) return Math.abs(Math.round((d12(dia) - d12(bot.sabadoRef)) / (7 * 864e5))) % 2 === 0;
  return true;
}

function useTodo(clinicas) {
  const [personal, setPersonal] = useState(null);
  const [aus, setAus] = useState(null);
  const [bot, setBot] = useState({});
  useEffect(() => onSnapshot(collection(db, 'personal'), (s) => setPersonal(s.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => a.nombre.localeCompare(b.nombre))), () => setPersonal([])), []);
  useEffect(() => onSnapshot(collection(db, 'ausencias'), (s) => setAus(s.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setAus([])), []);
  useEffect(() => {
    const paros = clinicas.map((c) => onSnapshot(doc(db, 'bot', c), (s) => setBot((b) => ({ ...b, [c]: s.data() || {} })), () => {}));
    return () => paros.forEach((p) => p());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicas.join(',')]);
  return { personal, aus, bot };
}

export default function Personal() {
  const { user, esAdmin: admin, clinicasActivas } = usePanel();
  const { personal, aus, bot } = useTodo(clinicasActivas);
  // Quién puede modificar horarios y vacaciones: los socios elegidos en config/personal.gestores (vacío = todos los socios)
  const [gestores, setGestores] = useState(null);
  useEffect(() => (admin ? onSnapshot(doc(db, 'config', 'personal'), (s) => setGestores(s.data()?.gestores || []), () => setGestores([])) : undefined), [admin]);
  const esAdmin = admin && (!gestores?.length || gestores.includes(user?.uid));
  const [tab, setTab] = useState('semana');
  const [pedir, setPedir] = useState(null);
  const [editar, setEditar] = useState(null);
  const yo = user?.displayName || user?.email || '';
  if (!personal || !aus) return <section><h1>Horarios y vacaciones</h1><p className="tenue">Cargando…</p></section>;
  const activos = personal.filter((p) => p.activo !== false);
  const delas = activos.filter((p) => !p.clinicas?.length || p.clinicas.some((c) => clinicasActivas.includes(c)));
  const mio = activos.find((p) => p.uid && p.uid === user?.uid);
  const pendientes = aus.filter((a) => a.estado === 'pendiente' && delas.some((p) => p.id === a.personaId));
  const ctx = { gestores, admin, onEditar: esAdmin ? (p) => setEditar(p) : null, personal: delas, todos: activos, aus, bot, clinicas: clinicasActivas, esAdmin, yo, uid: user?.uid, mio, setPedir: (x) => { setPedir(x); setTab('solicitudes'); } };

  return (
    <section>
      <div className="cabecera"><div><h1>Horarios y vacaciones</h1><p className="subtitulo">Quién está hoy, cuadrante semanal, vacaciones y días de cada persona</p></div>
        <div className="acciones"><button className="btn" onClick={() => ctx.setPedir({ personaId: mio?.id || '' })}>+ Pedir vacaciones / ausencia</button></div>
      </div>
      {!esAdmin && <p className="tenue" style={{ fontSize: '.82rem', marginTop: '-.4rem' }}>Puedes consultar el horario y pedir tus vacaciones; los cambios los aprueba {admin ? 'el responsable de personal' : 'la dirección'}.</p>}
      {editar && <FormPersona inicial={editar} onCerrar={() => setEditar(null)} />}
      <Hoy {...ctx} />
      <div className="filtros" style={{ margin: '.8rem 0', flexWrap: 'wrap' }}>
        {[['semana', 'Semana'], ['calendario', 'Calendario'], ['solicitudes', esAdmin ? 'Solicitudes' : 'Mis vacaciones'], ['equipo', esAdmin ? 'Equipo y días' : 'Mis días']].map(([k, t]) => (
          <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{t}{k === 'solicitudes' && esAdmin && pendientes.length > 0 && <span className="pend-badge">{pendientes.length}</span>}</button>
        ))}
      </div>
      {!activos.length && <Vacio esAdmin={esAdmin} />}
      {tab === 'semana' && <Semana {...ctx} />}
      {tab === 'calendario' && <Calendario {...ctx} />}
      {tab === 'solicitudes' && <Solicitudes {...ctx} pedir={pedir} setPedirLocal={setPedir} pendientes={pendientes} />}
      {tab === 'equipo' && <Equipo {...ctx} />}
    </section>
  );
}

// ---------- Hoy ----------
// Horario de apertura de cada clínica (para ver huecos sin nadie)
const L_V = (de, a) => ({ 1: [de, a], 2: [de, a], 3: [de, a], 4: [de, a], 5: [de, a] });
// Sale de los tramos de apertura de la ficha (bot.tramos: { díaSemana: [{ a, c }] } en minutos); sin tramos, lunes a viernes 10-20
const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const APERTURA = new Proxy({}, { get: (_, id) => {
  const t = ficha(id)?.bot?.tramos;
  if (!t || !Object.keys(t).length) return L_V('10:00', '20:00');
  return Object.fromEntries(Object.entries(t).filter(([, l]) => l?.length).map(([d, l]) => [d, [hhmm(Math.min(...l.map((x) => x.a))), hhmm(Math.max(...l.map((x) => x.c)))]]));
} });
const H0 = 9, H1 = 21; // escala de la barra
const pct = (h) => `${((Math.min(H1, Math.max(H0, h)) - H0) / (H1 - H0)) * 100}%`;
const ancho = (a, b) => `${((Math.min(H1, b) - Math.max(H0, a)) / (H1 - H0)) * 100}%`;
// Tramos sin cubrir dentro del horario de apertura
function huecos(clinica, dia, tramos) {
  const ap = APERTURA[clinica]?.[diaSem(dia)];
  if (!ap) return [];
  const [a, b] = ap.map(hNum);
  const ts = tramos.map((t) => [hNum(t.de), hNum(t.a)]).sort((x, y) => x[0] - y[0]);
  const out = []; let cur = a;
  for (const [x, y] of ts) { if (x > cur) out.push([cur, Math.min(x, b)]); cur = Math.max(cur, y); if (cur >= b) break; }
  if (cur < b) out.push([cur, b]);
  return out.filter(([x, y]) => y - x >= 0.25);
}
const hTxt = (h) => `${Math.floor(h)}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`;

// ---------- Hoy: barra de horas por clínica ----------
function Hoy({ personal, aus, clinicas, bot, onEditar }) {
  const hoy = hoyISO();
  const ahora = (() => { const d = new Date(); const [h, m] = new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Madrid' }).format(d).split(':'); return +h + +m / 60; })();
  const ausentes = personal.map((p) => [p, ausenciaDe(aus, p.id, hoy)]).filter(([, a]) => a);
  return (
    <div className="tarjeta vac-hoy2">
      <div className="vac-hoy2-cab"><b>Hoy · {fCorta(hoy)}</b>
        {ausentes.length > 0 && <span className="tenue">Fuera: {ausentes.map(([p, a]) => `${p.nombre.split(' ')[0]} (${TIPOS[a.tipo]?.[0].slice(2).toLowerCase()} hasta ${fCorta(a.hasta)})`).join(' · ')}</span>}
      </div>
      {clinicas.map((c) => {
        const abierta = abre(c, hoy, bot[c]);
        const ap = APERTURA[c]?.[diaSem(hoy)];
        const gente = personal.map((p) => [p, tramosDe(p, hoy).filter((t) => t.clinica === c)]).filter(([p, ts]) => ts.length && !ausenciaDe(aus, p.id, hoy));
        const hs = abierta ? huecos(c, hoy, gente.flatMap(([, ts]) => ts)) : [];
        return (
          <div key={c} className="vac-tl" style={{ '--cc': COLOR_CLIN[c] }}>
            <div className="vac-tl-clin"><b>{nombreClinica(c)}</b><small>{!abierta ? `Cerrado${festivo(c, hoy) ? ` · ${festivo(c, hoy)}` : ''}` : ap ? `${ap[0]}–${ap[1]}` : ''}</small></div>
            <div className="vac-tl-filas">
              {abierta && gente.map(([p, ts]) => (
                <div key={p.id} className="vac-tl-fila">
                  <span className={`vac-tl-nombre ${onEditar ? 'clic' : ''}`} onClick={() => onEditar?.(p)}>{p.nombre.split(' ')[0]}</span>
                  <div className="vac-tl-pista">
                    {ap && <i className="vac-tl-ap" style={{ left: pct(hNum(ap[0])), width: ancho(hNum(ap[0]), hNum(ap[1])) }} />}
                    {ts.map((t, i) => <i key={i} className="vac-tl-barra" style={{ left: pct(hNum(t.de)), width: ancho(hNum(t.de), hNum(t.a)) }}>{t.de.replace(/^0/, '')}–{t.a.replace(/^0/, '')}</i>)}
                    {ahora >= H0 && ahora <= H1 && <i className="vac-tl-ahora" style={{ left: pct(ahora) }} />}
                  </div>
                </div>
              ))}
              {abierta && hs.length > 0 && (
                <div className="vac-tl-fila">
                  <span className="vac-tl-nombre mal">Sin nadie</span>
                  <div className="vac-tl-pista">{hs.map(([x, y], i) => <i key={i} className="vac-tl-hueco" style={{ left: pct(x), width: ancho(x, y) }}>{hTxt(x)}–{hTxt(y)}</i>)}</div>
                </div>
              )}
              {abierta && !gente.length && !hs.length && <span className="vac-nadie">⚠️ Nadie en el horario</span>}
              {!abierta && <span className="tenue" style={{ fontSize: '.85rem' }}>—</span>}
            </div>
          </div>
        );
      })}
      <div className="vac-tl-escala"><span />{Array.from({ length: H1 - H0 + 1 }, (_, i) => <small key={i} style={{ left: pct(H0 + i) }}>{H0 + i}</small>)}</div>
    </div>
  );
}

// ---------- Cuadrante semanal (como el Excel: una tabla por clínica, personas × días) ----------
function Semana({ personal, aus, clinicas, bot, esAdmin, setPedir, onEditar }) {
  // AUD-PER-07: en domingo se enseña la semana que empieza mañana (no la que acaba de terminar)
  const lunes0 = diaSem(hoyISO()) === 0 ? masDias(hoyISO(), 1) : masDias(hoyISO(), -((diaSem(hoyISO()) + 6) % 7));
  const [lunes, setLunes] = useState(lunes0);
  const dias = rango(lunes, masDias(lunes, 5));
  const hoy = hoyISO();
  return (
    <>
      <div className="vac-sem-nav">
        <button className="btn sec" onClick={() => setLunes(masDias(lunes, -7))}>‹ Anterior</button>
        <div><b>Semana del {fCorta(lunes)} al {fCorta(masDias(lunes, 5))}</b>{lunes !== lunes0 && <button className="hz-link" onClick={() => setLunes(lunes0)}>volver a esta semana</button>}</div>
        <button className="btn sec" onClick={() => setLunes(masDias(lunes, 7))}>Siguiente ›</button>
      </div>
      {clinicas.map((c) => {
        const filas = personal.filter((p) => dias.some((d) => tramosDe(p, d).some((t) => t.clinica === c)));
        return (
          <div key={c} className="vac-cuad" style={{ '--cc': COLOR_CLIN[c] }}>
            <div className="vac-cuad-titulo">{nombreClinica(c)}</div>
            <div className="vac-cuad-scroll">
              <table>
                <thead>
                  <tr><th />{dias.map((d) => <th key={d} className={d === hoy ? 'hoy' : ''}>{['Dom', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'][diaSem(d)]}<small>{+d.slice(8)}/{+d.slice(5, 7)}</small></th>)}</tr>
                </thead>
                <tbody>
                  {filas.map((p) => (
                    <tr key={p.id}>
                      <th className={onEditar ? 'clic' : ''} onClick={() => onEditar?.(p)} title={onEditar ? 'Cambiar su horario' : ''}>{p.nombre.split(' ')[0]} <small>({PUESTOS[p.puesto]?.split('/')[0] || ''})</small>{onEditar && <span className="vac-lapiz">✎</span>}</th>
                      {dias.map((d) => {
                        const ts = tramosDe(p, d).filter((t) => t.clinica === c);
                        const a = ausenciaDe(aus, p.id, d, false);
                        if (!abre(c, d, bot[c])) return <td key={d} className="cerrado" />;
                        if (ts.length && a) return <td key={d} className={`aus ${a.estado}`} style={{ '--tc': TIPOS[a.tipo]?.[1] }}><b>{TIPOS[a.tipo]?.[0].slice(2)}</b>{a.estado === 'pendiente' && <small>pendiente</small>}<s>{ts.map((t) => `${t.de.slice(0, 5)}–${t.a.slice(0, 5)}`).join(' · ')}</s></td>;
                        if (!ts.length) return <td key={d} className={`vacia ${d === hoy ? 'hoy' : ''}`}>—</td>;
                        return <td key={d} className={`trabaja ${d === hoy ? 'hoy' : ''}`}>{ts.map((t, i) => <div key={i}>{t.de.replace(/:00$/, '')}–{t.a.replace(/:00$/, '')}</div>)}</td>;
                      })}
                    </tr>
                  ))}
                  <tr className="vac-cuad-cob">
                    <th>Cobertura</th>
                    {dias.map((d) => {
                      if (!abre(c, d, bot[c])) return <td key={d} className="cerrado">{festivo(c, d) || bot[c]?.cierres?.[d] || 'Cerrado'}</td>;
                      const ts = personal.filter((p) => !ausenciaDe(aus, p.id, d)).flatMap((p) => tramosDe(p, d).filter((t) => t.clinica === c));
                      const hs = huecos(c, d, ts);
                      const ap = APERTURA[c]?.[diaSem(d)];
                      return <td key={d} className={hs.length ? 'mal' : 'ok'}>{hs.length ? hs.map(([x, y], i) => <div key={i}>⚠️ {hTxt(x)}–{hTxt(y)} sin nadie</div>) : `✓ ${ap ? `${ap[0].replace(/:00$/, '')}–${ap[1].replace(/:00$/, '')}` : ''}`}</td>;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      {esAdmin && <p className="tenue" style={{ fontSize: '.8rem' }}>Toca el nombre de una persona para cambiar su horario. <button className="hz-link" onClick={() => setPedir({})}>Añadir una ausencia</button></p>}
    </>
  );
}

// ---------- Calendario del mes ----------
function Calendario({ personal, aus, clinicas, bot, esAdmin, mio, setPedir }) {
  const [mes, setMes] = useState(hoyISO().slice(0, 7));
  const [y, m] = mes.split('-').map(Number);
  const dias = rango(`${mes}-01`, iso(new Date(Date.UTC(y, m, 0, 12))));
  const mover = (n) => setMes(iso(new Date(Date.UTC(y, m - 1 + n, 1, 12))).slice(0, 7));
  const hoy = hoyISO();
  return (
    <div className="tarjeta">
      <div className="acciones" style={{ justifyContent: 'space-between' }}>
        <button className="btn sec mini" onClick={() => mover(-1)}>‹</button>
        <b style={{ textTransform: 'capitalize' }}>{d12(`${mes}-01`).toLocaleDateString('es-ES', { month: 'long', year: 'numeric', timeZone: 'UTC' })}</b>
        <button className="btn sec mini" onClick={() => mover(1)}>›</button>
      </div>
      <div className="vac-cal-scroll">
        <table className="vac-cal">
          <thead>
            <tr><th />{dias.map((d) => <th key={d} className={`${[0, 6].includes(diaSem(d)) ? 'fin' : ''} ${d === hoy ? 'hoy' : ''}`}><small>{'DLMXJVS'[diaSem(d)]}</small>{+d.slice(8)}</th>)}</tr>
          </thead>
          <tbody>
            {personal.map((p) => (
              <tr key={p.id}>
                <th title={PUESTOS[p.puesto] || ''}>{p.nombre.split(' ').slice(0, 2).join(' ')}</th>
                {dias.map((d) => {
                  const a = ausenciaDe(aus, p.id, d, false);
                  const ts = tramosDe(p, d);
                  const puede = esAdmin || p.id === mio?.id;
                  return (
                    <td key={d} className={`${[0, 6].includes(diaSem(d)) ? 'fin' : ''} ${festivo(p.clinicas?.[0], d) ? 'fest' : ''} ${d === hoy ? 'hoy' : ''} ${puede ? 'clic' : ''}`}
                      onClick={() => puede && !a && setPedir({ personaId: p.id, desde: d, hasta: d })} title={a ? `${TIPOS[a.tipo]?.[0]}${a.estado === 'pendiente' ? ' (pendiente)' : ''}${a.nota ? ` · ${a.nota}` : ''}` : ts.map((t) => `${nombreClinica(t.clinica)} ${t.de}–${t.a}`).join('\n')}>
                      {a ? <i className={`vac-aus ${a.estado}`} style={{ '--tc': TIPOS[a.tipo]?.[1] }} />
                        : ts.length ? <i className="vac-trab" style={{ background: COLOR_CLIN[ts[0].clinica] }}>{clinicas.length > 1 || ts.some((t) => t.clinica !== ts[0].clinica) ? INI_CLIN[ts[0].clinica] : ''}</i> : null}
                    </td>
                  );
                })}
              </tr>
            ))}
            {clinicas.map((c) => (
              <tr key={c} className="vac-cobertura">
                <th style={{ color: COLOR_CLIN[c] }}>{nombreClinica(c)}</th>
                {dias.map((d) => {
                  if (!abre(c, d, bot[c])) return <td key={d} className="fin" />;
                  const n = personal.filter((p) => tramosDe(p, d).some((t) => t.clinica === c) && !ausenciaDe(aus, p.id, d)).length;
                  return <td key={d} className={n === 0 ? 'cero' : ''} title={`${n} persona(s) en ${nombreClinica(c)}`}>{n}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="vac-leyenda">
        {Object.entries(TIPOS).map(([k, [t, col]]) => <span key={k}><i className="vac-aus aprobada" style={{ '--tc': col }} />{t}</span>)}
        <span><i className="vac-aus pendiente" style={{ '--tc': '#16a34a' }} />Pendiente de aprobar</span>
        <span className="tenue">Abajo: cuántas personas hay en cada clínica (en rojo, nadie).</span>
      </div>
    </div>
  );
}

// ---------- Solicitudes ----------
function Solicitudes({ personal, todos, aus, clinicas, bot, esAdmin, yo, uid, mio, pedir, setPedirLocal, pendientes }) {
  const anio = hoyISO().slice(0, 4);
  const visibles = esAdmin ? personal : personal.filter((p) => p.id === mio?.id);
  const deP = (id) => todos.find((p) => p.id === id);
  const resolver = (a, estado) => updateDoc(doc(db, 'ausencias', a.id), { estado, revisadoPor: yo, revisadoEn: new Date().toISOString() });
  const proximas = aus.filter((a) => a.estado === 'aprobada' && a.hasta >= hoyISO() && visibles.some((p) => p.id === a.personaId)).sort((a, b) => a.desde.localeCompare(b.desde));
  const mias = mio ? aus.filter((a) => a.personaId === mio.id).sort((a, b) => b.desde.localeCompare(a.desde)) : [];
  return (
    <>
      {pedir && <FormAusencia inicial={pedir} personal={esAdmin ? personal : visibles} aus={aus} bot={bot} clinicas={clinicas} esAdmin={esAdmin} yo={yo} uid={uid} onCerrar={() => setPedirLocal(null)} />}
      {!esAdmin && !mio && <div className="aviso-cita">Para pedir tus vacaciones desde aquí, un administrador tiene que vincular tu usuario a tu ficha en "Equipo y días".</div>}
      {mio && <TarjetaSaldo p={mio} aus={aus} anio={anio} />}
      {esAdmin && (
        <div className="tarjeta">
          <h3 style={{ marginTop: 0 }}>Pendientes de aprobar {pendientes.length > 0 && <span className="pend-badge">{pendientes.length}</span>}</h3>
          {!pendientes.length ? <p className="tenue">Nada pendiente.</p> : pendientes.sort((a, b) => a.desde.localeCompare(b.desde)).map((a) => {
            const p = deP(a.personaId);
            const choques = p ? conflictos(p, a, personal, aus, bot) : [];
            const s = p ? saldo(p, aus, a.desde.slice(0, 4)) : null;
            return (
              <div key={a.id} className="dia-fila">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b>{deP(a.personaId)?.nombre || a.persona}</b> · {TIPOS[a.tipo]?.[0]} · {fCorta(a.desde)}{a.hasta !== a.desde ? ` → ${fCorta(a.hasta)}` : ''} <span className="tenue">({p ? (a.tipo === 'vacaciones' ? `${diasGasta(p, a)} naturales · ${diasQueCuenta(p, a.desde, a.hasta)} de trabajo` : `${diasGasta(p, a)} días`) : '?'})</span>
                  {a.nota && <div className="tenue" style={{ fontSize: '.82rem' }}>“{a.nota}”</div>}
                  {s && a.tipo === 'vacaciones' && <div className={s.quedan - diasGasta(p, a) < 0 ? 'error' : 'tenue'} style={{ fontSize: '.78rem' }}>Le quedan {s.quedan} de {s.total} días naturales; con esto, {s.quedan - diasGasta(p, a)}.</div>}
                  {s && a.tipo === 'asuntos' && s.asuntosTotal != null && <div className={s.asuntosUsados >= s.asuntosTotal ? 'error' : 'tenue'} style={{ fontSize: '.78rem' }}>Asuntos propios usados este año: {s.asuntosUsados - diasGasta(p, a)} de {s.asuntosTotal}.</div>}
                  {choques.map((c, i) => <div key={i} className="aviso-txt" style={{ fontSize: '.78rem' }}>⚠️ {c}</div>)}
                  <div className="tenue" style={{ fontSize: '.72rem' }}>Pedido por {a.pedidoPor?.split(' ')[0]} el {new Date(a.creado).toLocaleDateString('es-ES')}</div>
                </div>
                <div className="acciones">
                  <button className="btn mini" onClick={() => resolver(a, 'aprobada')}>✓ Aprobar</button>
                  <button className="btn sec mini" onClick={() => resolver(a, 'rechazada')}>✕ Rechazar</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="tarjeta">
        <h3 style={{ marginTop: 0 }}>Próximas ausencias</h3>
        {!proximas.length ? <p className="tenue">Ninguna aprobada.</p> : proximas.map((a) => (
          <div key={a.id} className="dia-fila">
            <span style={{ flex: 1 }}><i className="vac-aus aprobada" style={{ '--tc': TIPOS[a.tipo]?.[1] }} /> <b>{deP(a.personaId)?.nombre || a.persona}</b> · {TIPOS[a.tipo]?.[0].slice(2)} · {fCorta(a.desde)}{a.hasta !== a.desde ? ` → ${fCorta(a.hasta)}` : ''}{a.nota ? <span className="tenue"> · {a.nota}</span> : null}</span>
            {esAdmin && <button className="hosp-x" title="Anular" onClick={() => window.confirm('¿Anular esta ausencia?') && deleteDoc(doc(db, 'ausencias', a.id))}>✕</button>}
          </div>
        ))}
      </div>
      {mias.length > 0 && !esAdmin && (
        <div className="tarjeta">
          <h3 style={{ marginTop: 0 }}>Mis solicitudes</h3>
          {mias.map((a) => (
            <div key={a.id} className="dia-fila">
              <span style={{ flex: 1 }}>{TIPOS[a.tipo]?.[0]} · {fCorta(a.desde)}{a.hasta !== a.desde ? ` → ${fCorta(a.hasta)}` : ''}</span>
              <span className={`chip ${a.estado === 'aprobada' ? 'bien' : a.estado === 'rechazada' ? 'mal' : 'aviso'}`}>{a.estado}</span>
              {a.estado === 'pendiente' && <button className="hosp-x" title="Cancelar" onClick={() => deleteDoc(doc(db, 'ausencias', a.id))}>✕</button>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// Avisos al pedir: deja una clínica sin nadie, o coincide con alguien del mismo puesto
function conflictos(p, a, personal, aus, bot) {
  const out = new Set();
  for (const d of rango(a.desde, a.hasta)) {
    for (const t of tramosDe(p, d)) {
      if (!abre(t.clinica, d, bot[t.clinica])) continue;
      const otros = personal.filter((o) => o.id !== p.id && tramosDe(o, d).some((x) => x.clinica === t.clinica) && !ausenciaDe(aus, o.id, d));
      if (!otros.length) out.add(`${nombreClinica(t.clinica)} se queda sin nadie el ${fCorta(d)}`);
    }
    const mismo = personal.filter((o) => o.id !== p.id && o.puesto === p.puesto && o.clinicas?.some((c) => p.clinicas?.includes(c)) && ausenciaDe(aus, o.id, d, false));
    mismo.forEach((o) => out.add(`Coincide con ${o.nombre.split(' ')[0]} (${PUESTOS[o.puesto] || o.puesto})`));
    if (out.size > 6) break;
  }
  return [...out].slice(0, 6);
}

function FormAusencia({ inicial, personal, aus, bot, esAdmin, yo, uid, onCerrar }) {
  const [f, setF] = useState({ personaId: inicial.personaId || personal[0]?.id || '', tipo: 'vacaciones', desde: inicial.desde || hoyISO(), hasta: inicial.hasta || inicial.desde || hoyISO(), nota: '' });
  const [aprobar, setAprobar] = useState(esAdmin);
  const p = personal.find((x) => x.id === f.personaId);
  const ok = p && f.desde && f.hasta >= f.desde;
  const n = ok ? diasGasta(p, f) : 0;
  const nTrab = ok ? diasQueCuenta(p, f.desde, f.hasta) : 0;
  const s = p ? saldo(p, aus, f.desde.slice(0, 4)) : null;
  const choques = ok ? conflictos(p, f, personal, aus, bot) : [];
  const guardar = async () => {
    await addDoc(collection(db, 'ausencias'), {
      personaId: p.id, persona: p.nombre, tipo: f.tipo, desde: f.desde, hasta: f.hasta, nota: f.nota.trim(),
      estado: esAdmin && aprobar ? 'aprobada' : 'pendiente', pedidoPor: yo, pedidoUid: uid, creado: new Date().toISOString(),
      ...(esAdmin && aprobar ? { revisadoPor: yo, revisadoEn: new Date().toISOString() } : {}),
    });
    onCerrar();
  };
  return (
    <div className="tarjeta vac-form">
      <h3 style={{ marginTop: 0 }}>{esAdmin ? 'Nueva ausencia' : 'Pedir vacaciones o ausencia'}</h3>
      {!personal.length ? <p className="tenue">No hay ficha a la que asignarla.</p> : <>
        <div className="ig-campos">
          {personal.length > 1 && <label><span>Persona</span><select value={f.personaId} onChange={(e) => setF({ ...f, personaId: e.target.value })}>{personal.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}</select></label>}
          <label><span>Tipo</span><select value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })}>{Object.entries(TIPOS).map(([k, [t]]) => <option key={k} value={k}>{t}</option>)}</select></label>
          <label><span>Desde</span><input type="date" className="campo" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value, hasta: f.hasta < e.target.value ? e.target.value : f.hasta })} /></label>
          <label><span>Hasta (incluido)</span><input type="date" className="campo" value={f.hasta} min={f.desde} onChange={(e) => setF({ ...f, hasta: e.target.value })} /></label>
          <label className="ancho"><span>Nota</span><input className="campo" value={f.nota} onChange={(e) => setF({ ...f, nota: e.target.value })} placeholder="Boda de mi hermana, médico…" /></label>
        </div>
        {ok && <p style={{ margin: '.5rem 0' }}>{f.tipo === 'vacaciones' ? <><b>{n} días naturales</b> <span className="tenue">({nTrab} de trabajo según su horario)</span>{s ? <span className="tenue"> · le quedan {s.quedan} de {s.total} → {s.quedan - n}{s.pedidos ? ` (y ${s.pedidos} pedidos sin aprobar)` : ''}</span> : null}</>
          : <><b>{nTrab} {nTrab === 1 ? 'día' : 'días'} de trabajo</b>{f.tipo === 'asuntos' && s?.asuntosTotal != null ? <span className="tenue"> · asuntos propios usados {s.asuntosUsados} de {s.asuntosTotal} (convenio: 1 al año, avisando con 7 días)</span> : null}</>}</p>}
        {choques.map((c, i) => <div key={i} className="aviso-txt" style={{ fontSize: '.85rem' }}>⚠️ {c}</div>)}
        {f.tipo === 'vacaciones' && s && s.quedan - n < 0 && <div className="error" style={{ fontSize: '.85rem' }}>Se pasa de los días que le corresponden.</div>}
        {f.tipo === 'asuntos' && s?.asuntosTotal != null && s.asuntosUsados + nTrab > s.asuntosTotal && <div className="error" style={{ fontSize: '.85rem' }}>El convenio da {s.asuntosTotal} día de asuntos propios al año.</div>}
        {f.tipo === 'asuntos' && ok && Math.round((d12(f.desde) - d12(hoyISO())) / 864e5) < 7 && <div className="aviso-txt" style={{ fontSize: '.85rem' }}>El convenio pide avisar con 7 días de antelación (salvo fuerza mayor).</div>}
        <div className="acciones" style={{ marginTop: '.6rem' }}>
          {esAdmin && <label className="tenue"><input type="checkbox" checked={aprobar} onChange={(e) => setAprobar(e.target.checked)} /> Aprobada ya</label>}
          <button className="btn" disabled={!ok} onClick={guardar}>{esAdmin && aprobar ? 'Guardar' : 'Enviar solicitud'}</button>
          <button className="btn sec" onClick={onCerrar}>Cancelar</button>
        </div>
      </>}
    </div>
  );
}

function TarjetaSaldo({ p, aus, anio }) {
  const s = saldo(p, aus, anio);
  return (
    <div className="ig-resumen" style={{ marginBottom: '.8rem' }}>
      <div><small>Vacaciones {anio}</small><b>{s.total}</b><span>días naturales{s.conv ? ` (convenio${s.frac < 1 ? ', prorrateado' : ''})` : ''}</span></div>
      <div><small>Ya disfrutados</small><b>{s.disfrutados}</b><span>{s.aprobados - s.disfrutados} aprobados por venir</span></div>
      <div><small>Pedidos sin aprobar</small><b>{s.pedidos}</b><span>{s.asuntosTotal != null ? `Asuntos propios: ${s.asuntosUsados} de ${s.asuntosTotal}` : ''}</span></div>
      <div className={s.quedan < 0 ? 'mal' : ''}><small>Te quedan</small><b>{s.quedan}</b><span>por pedir</span></div>
    </div>
  );
}

// ---------- Equipo: fichas, horario base y saldo ----------
function Equipo({ todos, personal, aus, esAdmin, mio, gestores, uid }) {
  const [anio, setAnio] = useState(hoyISO().slice(0, 4));
  const [edit, setEdit] = useState(null);
  const lista = esAdmin ? todos : personal.filter((p) => p.id === mio?.id);
  return (
    <>
      {edit && <FormPersona inicial={edit} onCerrar={() => setEdit(null)} />}
      <div className="tarjeta" style={{ padding: 0 }}>
        <div className="acciones" style={{ padding: '.7rem 1rem', justifyContent: 'space-between' }}>
          <label className="tenue">Año <select value={anio} onChange={(e) => setAnio(e.target.value)}>{[+hoyISO().slice(0, 4) - 1, +hoyISO().slice(0, 4), +hoyISO().slice(0, 4) + 1].map((y) => <option key={y}>{y}</option>)}</select></label>
          {esAdmin && <span className="acciones"><button className="btn mini" onClick={() => setEdit({})}>+ Persona</button></span>}
        </div>
        <div className="vac-equipo">
          <div className="vac-eq-cab"><span>Persona</span><span>Horas año / máx.</span><span>Le tocan</span><span>Disfrutados</span><span>Aprobados</span><span>Pedidos</span><span>Quedan</span><span /></div>
          {lista.map((p) => {
            const s = saldo(p, aus, anio);
            const ha = horasAnio(p, aus, anio);
            const hs = [1, 2, 3, 4, 5, 6].reduce((n, d) => n + (p.horario?.[d] || []).reduce((m, t) => m + horas(t) * (t.semanas === 'A' || t.semanas === 'B' ? 0.5 : 1), 0), 0);
            return (
              <div key={p.id} className="vac-eq-fila">
                <span><b>{p.nombre}</b><small className="tenue">{PUESTOS[p.puesto] || ''}{p.fechaAlta ? ` · desde ${fCorta(p.fechaAlta)}` : ''}{p.fechaBaja ? ` · hasta ${fCorta(p.fechaBaja)}` : ''}{p.clinicas?.length ? ` · ${p.clinicas.map(nombreClinica).join(', ')}` : ''}{p.uid ? ' · 🔗' : ''}</small></span>
                <span className="tenue" style={{ fontSize: '.8rem' }}>{hs ? <>{fmtH(hs)} h/sem<br /><span className={s.conv && ha.max && ha.h > ha.max ? 'error' : ''}>{ha.h}{s.conv && ha.max ? ` / ${ha.max}` : ''} h/año</span></> : 'sin horario'}</span>
                <span title={s.conv ? `Convenio: 30 naturales${s.frac < 1 ? ` × ${Math.round(s.frac * 100)} % del año` : ''}` : 'Fijado a mano'}>{s.total}{s.asuntosTotal != null ? <small className="tenue"> +{s.asuntosTotal} AP</small> : null}</span><span>{s.disfrutados}</span><span>{s.aprobados}</span><span>{s.pedidos}</span>
                <span className={s.quedan < 0 ? 'mal' : ''}><b>{s.quedan}</b></span>
                <span>{esAdmin && <button className="btn sec mini" onClick={() => setEdit(p)}>✎</button>}</span>
              </div>
            );
          })}
        </div>
      </div>
      {esAdmin && <Gestores gestores={gestores || []} uid={uid} />}
      {esAdmin && todos.some((p) => p.activo === false) && <p className="tenue" style={{ fontSize: '.8rem' }}>Hay personas dadas de baja ocultas en el resto de vistas.</p>}
    </>
  );
}

function Gestores({ gestores, uid }) {
  const [admins, setAdmins] = useState([]);
  useEffect(() => onSnapshot(query(collection(db, 'usuarios'), where('org', '==', orgActual())), (s) => setAdmins(s.docs.map((d) => ({ uid: d.id, ...d.data() })).filter((u) => u.rol === 'admin')), () => {}), []);
  const [sel, setSel] = useState(null);
  const lista = sel ?? (gestores.length ? gestores : admins.map((a) => a.uid));
  const toggle = (u) => setSel(lista.includes(u) ? lista.filter((x) => x !== u) : [...lista, u]);
  const guardar = async () => { const l = lista.includes(uid) ? lista : [...lista, uid]; await setDoc(doc(db, 'config', 'personal'), { gestores: l }, { merge: true }); setSel(null); };
  return (
    <div className="tarjeta">
      <h3 style={{ marginTop: 0 }}>🔒 Quién puede modificar horarios y vacaciones</h3>
      <p className="tenue" style={{ fontSize: '.82rem' }}>Solo estos socios pueden cambiar horarios, fichas y aprobar o poner vacaciones. El resto (socios y equipo) solo consulta y pide sus propias vacaciones.</p>
      <div className="filtros">{admins.map((a) => <button key={a.uid} className={lista.includes(a.uid) ? 'on' : ''} disabled={a.uid === uid} onClick={() => toggle(a.uid)} title={a.uid === uid ? 'Tú siempre (para no quedarte fuera)' : ''}>{lista.includes(a.uid) ? '✓ ' : ''}{a.nombre || a.email || a.uid}</button>)}</div>
      {sel && <button className="btn mini" style={{ marginTop: '.5rem' }} onClick={guardar}>Guardar</button>}
    </div>
  );
}

function FormPersona({ inicial, onCerrar }) {
  const caja = useRef(null);
  useEffect(() => { caja.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [inicial]);
  const [usuarios, setUsuarios] = useState([]);
  useEffect(() => onSnapshot(query(collection(db, 'usuarios'), where('org', '==', orgActual())), (s) => setUsuarios(s.docs.map((d) => ({ uid: d.id, ...d.data() }))), () => {}), []);
  const [f, setF] = useState({ nombre: inicial.nombre || '', puesto: inicial.puesto || 'atv', clinicas: inicial.clinicas || [], uid: inicial.uid || '', computo: inicial.computo || (inicial.puesto === 'socio' ? 'manual' : 'convenio'), diasAnuales: String(inicial.computo === 'manual' ? inicial.diasAnuales ?? 30 : 30), fechaAlta: inicial.fechaAlta || '', fechaBaja: inicial.fechaBaja || '', activo: inicial.activo !== false, horario: inicial.horario || {} });
  const setTramo = (d, i, k, v) => { const l = [...(f.horario[d] || [])]; l[i] = { ...l[i], [k]: v }; setF({ ...f, horario: { ...f.horario, [d]: l } }); };
  const addTramo = (d) => { const l = f.horario[d] || []; const prev = l[l.length - 1]; setF({ ...f, horario: { ...f.horario, [d]: [...l, prev ? { clinica: prev.clinica, de: '16:00', a: '20:00' } : { clinica: f.clinicas[0] || CLINICAS[0].id, de: '10:00', a: d === 6 ? '14:00' : '20:00' }] } }); };
  const delTramo = (d, i) => setF({ ...f, horario: { ...f.horario, [d]: (f.horario[d] || []).filter((_, j) => j !== i) } });
  const copiarLunes = () => { const l = f.horario[1] || []; setF({ ...f, horario: { ...f.horario, 2: l, 3: l, 4: l, 5: l } }); };
  // AUD-PER-04/05: baja no anterior al alta, nombre acotado, días al año 0–60
  const errP = primerError(validarTexto(f.nombre, { etiqueta: 'El nombre', max: 80, obligatorio: true }),
    f.fechaAlta ? validarFecha(f.fechaAlta, { etiqueta: 'La fecha de alta' }) : null, f.fechaBaja ? validarFecha(f.fechaBaja, { etiqueta: 'La fecha de baja' }) : null,
    f.fechaAlta && f.fechaBaja && f.fechaBaja < f.fechaAlta ? { ok: false, error: 'La fecha de baja no puede ser anterior a la de alta' } : null,
    f.computo === 'manual' ? validarCantidad(f.diasAnuales, { etiqueta: 'Los días al año', min: 0, max: 60, obligatorio: true }) : null);
  const borrar = async () => {
    if (!window.confirm(`¿Borrar a ${inicial.nombre} del cuadrante? Sus ausencias pasadas se conservan. Si solo se ha ido, mejor marca «Ya no trabaja aquí».`)) return;
    await deleteDoc(doc(db, 'personal', inicial.id));
    onCerrar();
  };
  const guardar = async () => {
    if (errP) return;
    const d = { nombre: f.nombre.trim(), puesto: f.puesto, clinicas: f.clinicas, uid: f.uid || null, computo: f.computo, diasAnuales: f.computo === 'manual' ? Number(f.diasAnuales) || 0 : 30, fechaAlta: f.fechaAlta || null, fechaBaja: f.fechaBaja || null, activo: f.activo, horario: f.horario };
    if (inicial.id) await updateDoc(doc(db, 'personal', inicial.id), d); else await addDoc(collection(db, 'personal'), d);
    onCerrar();
  };
  const semA = (() => { let s = hoyISO(); while (diaSem(s) !== 6) s = masDias(s, 1); return semanaISO(s) % 2 === 0 ? s : masDias(s, 7); })();
  return (
    <div className="tarjeta vac-form" ref={caja}>
      <h3 style={{ marginTop: 0 }}>{inicial.id ? `Editar ${inicial.nombre}` : 'Nueva persona'}</h3>
      <div className="ig-campos">
        <label><span>Nombre</span><input className="campo" maxLength={80} value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} autoFocus={!inicial.id} /></label>
        <label><span>Puesto</span><select value={f.puesto} onChange={(e) => setF({ ...f, puesto: e.target.value })}>{Object.entries(PUESTOS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}</select></label>
        <label><span>Vacaciones</span><select value={f.computo} onChange={(e) => setF({ ...f, computo: e.target.value })}><option value="convenio">Convenio: 30 naturales + 1 asuntos propios</option><option value="manual">A mano (socios, otros)</option></select></label>
        {f.computo === 'manual' && <label><span>Días naturales al año</span><input className="campo" value={f.diasAnuales} onChange={(e) => setF({ ...f, diasAnuales: e.target.value })} inputMode="numeric" /></label>}
        <label title="Si entró este año, las vacaciones se prorratean desde esta fecha"><span>Fecha de alta</span><input type="date" className="campo" value={f.fechaAlta} onChange={(e) => setF({ ...f, fechaAlta: e.target.value })} /></label>
        <label title="Si se va, se prorratean hasta esta fecha"><span>Fecha de baja (si se va)</span><input type="date" className="campo" min={f.fechaAlta || undefined} value={f.fechaBaja} onChange={(e) => setF({ ...f, fechaBaja: e.target.value })} /></label>
        <label><span>Su usuario del panel (para que pida sus vacaciones)</span><select value={f.uid} onChange={(e) => setF({ ...f, uid: e.target.value })}><option value="">— sin vincular —</option>{usuarios.map((u) => <option key={u.uid} value={u.uid}>{u.nombre || u.email || u.uid}</option>)}</select></label>
        <div className="ancho"><span className="hz-lbl">Clínicas</span><div className="filtros">{CLINICAS.map((c) => <button type="button" key={c.id} className={f.clinicas.includes(c.id) ? 'on' : ''} onClick={() => setF({ ...f, clinicas: f.clinicas.includes(c.id) ? f.clinicas.filter((x) => x !== c.id) : [...f.clinicas, c.id] })}>{c.nombre}</button>)}</div></div>
      </div>
      <h4 style={{ margin: '1rem 0 .3rem' }}>Horario base <span className="tenue" style={{ fontWeight: 400, fontSize: '.8rem' }}>(cada día, uno o varios tramos y en qué clínica)</span></h4>
      <div className="vac-horario-ed">
        {DIAS.map(([d, t]) => (
          <div key={d} className="vac-hed-dia">
            <b>{t}</b>
            <div className="vac-hed-tramos">
              {(f.horario[d] || []).map((tr, i) => (
                <div key={i} className="vac-hed-tramo">
                  <select value={tr.clinica} onChange={(e) => setTramo(d, i, 'clinica', e.target.value)}>{CLINICAS.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}</select>
                  <input type="time" className="campo" value={tr.de} onChange={(e) => setTramo(d, i, 'de', e.target.value)} step="900" />
                  <span>–</span>
                  <input type="time" className="campo" value={tr.a} onChange={(e) => setTramo(d, i, 'a', e.target.value)} step="900" />
                  {d === 6 && <select value={tr.semanas || 'todas'} onChange={(e) => setTramo(d, i, 'semanas', e.target.value)} title={`Semana A: sábados como el ${fCorta(semA)}. Semana B: los otros.`}><option value="todas">Todos los sábados</option><option value="13">1º y 3º del mes</option><option value="24">2º y 4º del mes</option><option value="A">Alternos A (como el {fCorta(semA).replace(/^\S+ /, '')})</option><option value="B">Alternos B</option></select>}
                  <button type="button" className="hosp-x" onClick={() => delTramo(d, i)}>✕</button>
                </div>
              ))}
              <button type="button" className="hz-link" onClick={() => addTramo(d)}>+ tramo</button>
              {d === 1 && (f.horario[1] || []).length > 0 && <button type="button" className="hz-link" onClick={copiarLunes}>copiar el lunes a martes–viernes</button>}
            </div>
          </div>
        ))}
      </div>
      <div className="acciones" style={{ marginTop: '.8rem' }}>
        <button className="btn" disabled={!f.nombre.trim() || !!errP} onClick={guardar}>Guardar</button>
        <button className="btn sec" onClick={onCerrar}>Cancelar</button>
        {inicial.id && <button className="btn sec" onClick={borrar}>Borrar persona</button>}
        {errP && f.nombre && <span className="error" role="alert">{errP}</span>}
        {inicial.id && <label className="tenue" style={{ marginLeft: 'auto' }}><input type="checkbox" checked={!f.activo} onChange={(e) => setF({ ...f, activo: !e.target.checked })} /> Ya no trabaja aquí</label>}
      </div>
    </div>
  );
}

function Vacio({ esAdmin }) {
  return (
    <div className="tarjeta">
      <p style={{ marginTop: 0 }}>Todavía no hay nadie dado de alta.</p>
      {esAdmin && <p className="tenue" style={{ fontSize: '.85rem' }}>Ve a la pestaña «Equipo» y pulsa «+ Persona» para añadir a cada persona con su horario.</p>}
    </div>
  );
}

// Para avisos en el menú: solicitudes pendientes (solo admin)
export function usePendientesVacaciones(activo) {
  const [n, setN] = useState(0);
  useEffect(() => (activo ? onSnapshot(collection(db, 'ausencias'), (s) => setN(s.docs.filter((d) => d.data().estado === 'pendiente').length), () => {}) : undefined), [activo]);
  return n;
}
